import { updTitle } from "@/lib/receiving";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Loader2, Search, SlidersHorizontal, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  PHOTO_CATEGORIES,
  PHOTO_CATEGORY_TITLES,
  listReviewDeliveries,
  listReviewOrganizations,
  type ReviewDeliveryCard,
} from "@/api/receiving";
import { DateRangePicker } from "@/components/DateRangePicker";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { localISODate } from "@/lib/date-range";
import { fmtDate } from "@/lib/format";
import { useDebounced } from "@/lib/use-debounced";
import { cn } from "@/lib/utils";
import { OkkBadge } from "@/pages/receiving/OkkBadge";

/**
 * Журнал приёмки — десктопный просмотр ПРИХОДОВ для оператора 1С (read-only).
 *
 * Строка — приход (накладная), а не заказ: по одному заказу приезжает
 * несколько машин, и склеенная строка прятала, что именно приняли этой.
 * Данные целиком из базы портала — журнал в 1С не ходит вовсе, раньше каждый
 * его показ стоил обхода заказов плюс регистра остатков.
 *
 * Сортировка, окно дат и группировка — по ДАТЕ УПД: приход стал корневым
 * документом, и оператор ищет поставку по её накладной, а не по тому, когда
 * кладовщик открыл экран. У приёмок прежнего раздела даты УПД нет — там
 * остаётся дата приёмки, иначе они выпали бы из журнала совсем.
 */
const LS = {
  q: "wh_rcvr_q",
  org: "wh_rcvr_org",
  status: "wh_rcvr_status",
};
const lsGet = (k: string) => {
  try {
    return localStorage.getItem(k) ?? "";
  } catch {
    return "";
  }
};

const WEEKDAYS = ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"];

function dayHeading(iso: string): string {
  if (!iso) return "Без даты";
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  return `${WEEKDAYS[d.getDay()]} · ${fmtDate(iso)}`;
}

const CATEGORY_SHORT: Record<string, string> = {
  upd: "УПД",
  certificates: "Серт",
  goods: "Товар",
};

const STATUS_OPTIONS: { value: "" | "open" | "completed"; label: string }[] = [
  { value: "", label: "Все" },
  { value: "open", label: "В работе" },
  { value: "completed", label: "Завершены" },
];

export function ReviewListPage(): React.ReactElement {
  const navigate = useNavigate();
  const [rawQuery, setRawQuery] = useState(() => lsGet(LS.q));
  const query = useDebounced(rawQuery.trim(), 350);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [orgRef, setOrgRef] = useState<string>(() => lsGet(LS.org));
  const [status, setStatus] = useState<"" | "open" | "completed">(
    () => lsGet(LS.status) as "" | "open" | "completed",
  );
  const [dateFrom, setDateFrom] = useState<string>(() => localISODate(-6));
  const [dateTo, setDateTo] = useState<string>(() => localISODate());

  useEffect(() => {
    try {
      localStorage.setItem(LS.q, rawQuery);
      localStorage.setItem(LS.org, orgRef);
      localStorage.setItem(LS.status, status);
    } catch {
      /* приватный режим — не критично */
    }
  }, [rawQuery, orgRef, status]);

  const orgsQ = useQuery({
    queryKey: ["rcv-review-orgs"],
    queryFn: () => listReviewOrganizations(),
    enabled: filtersOpen,
    staleTime: 5 * 60_000,
  });

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
  } = useInfiniteQuery({
    queryKey: ["rcv-review-deliveries", query, orgRef, status, dateFrom, dateTo],
    queryFn: ({ pageParam }) =>
      listReviewDeliveries({
        q: query,
        organization_ref: orgRef,
        delivery_status: status,
        date_from: dateFrom,
        date_to: dateTo,
        skip: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.has_more
        ? allPages.reduce((n, p) => n + p.items.length, 0)
        : undefined,
  });

  const rows = data?.pages.flatMap((p) => p.items) ?? [];
  const activeFilters = (orgRef ? 1 : 0) + (status ? 1 : 0);

  // Группировка по дате УПД — той же оси, по которой сортирует и фильтрует
  // бэк. У приёмок прежнего раздела её нет, и там остаётся дата приёмки.
  const groups: { day: string; items: ReviewDeliveryCard[] }[] = [];
  for (const d of rows) {
    const day = (d.incoming_date || d.created_at || "").slice(0, 10);
    const last = groups[groups.length - 1];
    if (last && last.day === day) last.items.push(d);
    else groups.push({ day, items: [d] });
  }

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    });
    io.observe(el);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const rangeIs = (from: string, to: string) => dateFrom === from && dateTo === to;
  const presets: { label: string; from: string; to: string }[] = [
    { label: "Сегодня", from: localISODate(), to: localISODate() },
    { label: "Вчера", from: localISODate(-1), to: localISODate(-1) },
    { label: "Неделя", from: localISODate(-6), to: localISODate() },
    { label: "Месяц", from: localISODate(-29), to: localISODate() },
  ];

  return (
    // Во всю ширину: строка приходa несёт восемь колонок (УПД, заказ,
    // поставщик, позиции, накладная, фото, кто принял), и в узкой колонке
    // они наезжают друг на друга.
    <div className="space-y-3">
      <h1 className="text-xl font-semibold">Журнал приёмки</h1>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-64">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={rawQuery}
            onChange={(e) => setRawQuery(e.target.value)}
            placeholder="Заказ, УПД или накладная"
            className="pl-8"
          />
          {rawQuery && (
            <button
              type="button"
              onClick={() => setRawQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
              aria-label="Очистить"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
        <div className="w-64">
          <DateRangePicker
            from={dateFrom}
            to={dateTo}
            onChange={(f, t) => {
              setDateFrom(f);
              setDateTo(t);
            }}
          />
        </div>
        {presets.map((pr) => (
          <Chip
            key={pr.label}
            active={rangeIs(pr.from, pr.to)}
            onClick={() => {
              setDateFrom(pr.from);
              setDateTo(pr.to);
            }}
          >
            {pr.label}
          </Chip>
        ))}
        <Button
          variant={activeFilters ? "default" : "outline"}
          size="icon"
          onClick={() => setFiltersOpen((s) => !s)}
          aria-label="Фильтры"
          className="relative ml-auto shrink-0"
        >
          <SlidersHorizontal className="size-4" />
          {activeFilters > 0 && (
            <span className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">
              {activeFilters}
            </span>
          )}
        </Button>
      </div>

      {query && (
        <p className="text-xs text-muted-foreground">
          Поиск ищет по всем приходам — окно дат снято.
        </p>
      )}

      {filtersOpen && (
        <Card className="space-y-3 p-3">
          <div>
            <div className="mb-1 text-xs font-medium text-muted-foreground">
              Состояние приёмки
            </div>
            <div className="flex flex-wrap gap-1.5">
              {STATUS_OPTIONS.map((o) => (
                <Chip
                  key={o.value || "all"}
                  active={status === o.value}
                  onClick={() => setStatus(o.value)}
                >
                  {o.label}
                </Chip>
              ))}
            </div>
          </div>
          <div>
            <div className="mb-1 text-xs font-medium text-muted-foreground">
              Организация
            </div>
            <div className="flex flex-wrap gap-1.5">
              {orgsQ.isLoading && (
                <Loader2 className="size-4 animate-spin text-muted-foreground" />
              )}
              {orgsQ.data?.map((o) => (
                <Chip
                  key={o.ref}
                  active={orgRef === o.ref}
                  onClick={() => setOrgRef(orgRef === o.ref ? "" : o.ref)}
                >
                  {o.name}
                </Chip>
              ))}
            </div>
          </div>
          {activeFilters > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setOrgRef("");
                setStatus("");
              }}
            >
              Сбросить фильтры
            </Button>
          )}
        </Card>
      )}

      {isLoading && (
        <div className="flex justify-center py-10 text-muted-foreground">
          <Loader2 className="size-6 animate-spin" />
        </div>
      )}
      {isError && (
        <Card className="border-destructive/50 p-3 text-sm text-destructive">
          Не удалось загрузить журнал: {(error as Error)?.message ?? "ошибка"}
        </Card>
      )}
      {!isLoading && !isError && rows.length === 0 && (
        <p className="py-10 text-center text-sm text-muted-foreground">
          {query ? `По запросу «${query}» приходов нет` : "Приходов в выбранные даты нет"}
        </p>
      )}

      {groups.map((g) => (
        <div key={g.day || "none"} className="space-y-1.5">
          <h2 className="pt-2 text-sm font-semibold text-muted-foreground">
            {dayHeading(g.day)}
          </h2>
          <Card className="divide-y overflow-hidden">
            {g.items.map((d) => (
              <DeliveryRow
                key={d.id}
                row={d}
                onClick={() => navigate(`/receiving-review/deliveries/${d.id}`)}
              />
            ))}
          </Card>
        </div>
      ))}

      <div ref={sentinelRef} className="h-6" />
      {isFetchingNextPage && (
        <div className="flex justify-center py-2 text-muted-foreground">
          <Loader2 className="size-5 animate-spin" />
        </div>
      )}
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 py-1 text-xs transition-colors",
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-input bg-background text-foreground hover:bg-accent",
      )}
    >
      {children}
    </button>
  );
}

/** Состояние прихода: идёт приёмка / завершена, и что с накладной. */
export function DeliveryStatusBadge({
  row,
}: {
  row: Pick<ReviewDeliveryCard, "status" | "receipt_number" | "receipt_posted">;
}): React.ReactElement {
  if (row.status !== "completed") {
    return (
      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-700">
        в работе
      </span>
    );
  }
  if (!row.receipt_number) {
    // Завершён без накладной — так бывает только у приходов без позиций.
    return (
      <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
        без накладной
      </span>
    );
  }
  return (
    <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700">
      {row.receipt_number}
      {row.receipt_posted ? " ✓" : ""}
    </span>
  );
}

function DeliveryRow({
  row,
  onClick,
}: {
  row: ReviewDeliveryCard;
  onClick: () => void;
}): React.ReactElement {
  const counts = PHOTO_CATEGORIES.filter((c) => (row.photo_counts[c] ?? 0) > 0)
    .map((c) => `${CATEGORY_SHORT[c] ?? PHOTO_CATEGORY_TITLES[c]} ${row.photo_counts[c]}`)
    .join(" · ");
  return (
    <div className="flex items-center hover:bg-accent">
    <button
      type="button"
      onClick={onClick}
      className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-left transition-colors"
    >
      <span className="w-32 shrink-0">
        <span className="font-semibold">
          {updTitle(row)}
        </span>
        {row.incoming_date && (
          <span className="block text-[11px] text-muted-foreground">
            от {fmtDate(row.incoming_date)}
          </span>
        )}
      </span>
      <span className="w-28 shrink-0 text-sm text-muted-foreground">
        {row.order_number}
      </span>
      <span className="min-w-0 flex-1 truncate text-sm">
        {row.organization_name}
        <span className="text-muted-foreground"> ← {row.kontragent_name}</span>
      </span>
      <span className="w-24 shrink-0 text-right text-xs text-muted-foreground">
        {row.positions} поз · {row.places} мест
      </span>
      <DeliveryStatusBadge row={row} />
      <span className="w-36 shrink-0 text-right text-xs text-muted-foreground">
        {counts || "фото нет"}
      </span>
      <span className="w-24 shrink-0 truncate text-right text-xs text-muted-foreground">
        {row.received_by}
      </span>
    </button>
    {/* Вне <button>: бейдж — ссылка на проверку, вложенная интерактивность недопустима. */}
    {row.okk && <OkkBadge okk={row.okk} className="mr-3 shrink-0" />}
    </div>
  );
}
